import React from 'react';
import { toast } from 'react-toastify';
import moment from 'moment';
import * as Sentry from '@sentry/react';

import { DATE_FORMAT_YMD } from '../constants/dateFormatsConst';
import { STORAGE_TOGGLE_SALES_DEMO_MODE } from '../constants/storageConst';
import { isPostHogFeatureEnabled } from './postHogService';

const EXTENDED_ERROR_REPORTING_FLAG = 'autoport-extended-error-reporting';

// Only forward shapes that represent real exceptions/API errors.
// Plain strings are skipped — most are validation/UX messages
// ("Please enter X", "File too large") that would flood Sentry with noise.
// Request cancellations are intentional, not failures; the Api.js wrapper rejects
// them as a contentless `new Error('')` sentinel. Skip those (and any empty-message
// error) — they carry no diagnostic value and only add noise to Sentry.
function isNonReportableError(err) {
  return (
    !err.message ||
    err.message === 'canceled' ||
    err.code === 'ERR_CANCELED' ||
    err.name === 'CanceledError'
  );
}

function forwardToSentry(msg) {
  const tags = { handler: 'showMsg' };
  if (msg instanceof Error) {
    // Skip if already captured at the Api.js boundary, or a cancellation/empty error.
    if (msg.__sentryCaptured || isNonReportableError(msg)) {
      return;
    }
    Sentry.captureException(msg, { tags });
  } else if (Array.isArray(msg) && msg.length > 0) {
    Sentry.captureException(new Error(msg.join('; ')), { tags, extra: { original: msg } });
  } else if (msg && typeof msg === 'object' && msg.message) {
    Sentry.captureException(new Error(msg.message), { tags, extra: { original: msg } });
  }
}

export const toProperCase = (str = '') => {
  return str?.replace(/\w\S*/g, function (txt) {
    return txt?.charAt(0)?.toUpperCase() + txt?.substr(1)?.toLowerCase();
  });
};

// tree animation functions starts
function show(elem, t) {
  var getHeight = function () {
    elem.style.display = 'block';
    var height = `${elem.scrollHeight}px`;
    elem.style.display = '';
    return height;
  };
  var height = getHeight();
  elem.classList.add('expand');
  elem.style.height = height;
  window.setTimeout(function () {
    elem.style.height = '';
  }, t);
}

function hide(elem, t) {
  elem.style.height = `${elem.scrollHeight}px`;
  window.setTimeout(function () {
    elem.style.height = '0';
  }, 1);
  window.setTimeout(function () {
    elem.classList.remove('expand');
  }, t);
}

export const toggleElement = (elem, t = 350) => {
  if (elem?.classList?.contains('expand')) {
    hide(elem, t);
    return;
  }
  show(elem, t);
};
// tree animation function ends

export const showMsg = (msg, type = 'error', position = 'bottom-right', prefix = '') => {
  let messages = [];
  if (typeof msg === 'string') {
    messages = msg;
  } else if (Array.isArray(msg)) {
    messages = msg.map(m => (
      <div key={`toast-err-msg-${m}`}>
        {prefix ? (
          <ul style={{ marginBottom: 0, paddingLeft: 10, listStyleType: prefix }}>
            <li>{m}</li>
          </ul>
        ) : (
          <>
            {m}
            <br />
          </>
        )}
      </div>
    ));
  } else if (typeof msg === 'object' && typeof msg.message !== 'undefined') {
    if (msg?.message) {
      console.error(msg);
    }
    messages = msg.message;
  } else {
    console.error(msg);
    // type = "error";
    // messages = "Oops Some error occured.";
  }
  if (type === 'error' && isPostHogFeatureEnabled(EXTENDED_ERROR_REPORTING_FLAG)) {
    forwardToSentry(msg);
  }

  if (messages.length) {
    switch (type) {
      case 'error':
        toast.error(<div>{messages}</div>, { toastId: `${msg}`, position: position });
        break;
      case 'success':
        toast.success(<div>{messages}</div>, { toastId: `${msg}`, position: position });
        break;
      case 'warn':
        toast.warn(<div>{messages}</div>, { toastId: `${msg}`, position: position });
        break;
      case 'info':
        toast.info(<div>{messages}</div>, { toastId: `${msg}`, position: position });
        break;
      default:
        console.error(msg);
        // toast.error(<div>{messages}</div>, { toastId: `UNKNOWN_ERROR`, position: 'bottom-right' });
        break;
    }
  }
};

/**
 * Returns filename from content-disposition from response headers
 * @param {object} headers - response headers
 */
export const getFilename = headers => {
  let fileName = null;
  if (headers['content-disposition']) {
    const fileNameMatch = headers['content-disposition'].match(/filename=(.+)/);
    if (fileNameMatch.length === 2) {
      fileName = fileNameMatch[1];
    }
  }
  return fileName;
};

/**
 * Get vehicle name, model and vin details from vehicle
 * @param {string} vehName - vehicle data like "(ACURA - ILX)19UDE2F7XXXXXX015"
 */
export const extractVehicle = vehName => {
  let pattern = /^\((.+)-(.+)\)(.+)$/;
  let data = vehName?.match(pattern);
  return {
    name: data !== null && data?.length > 1 ? data[1]?.trim() : vehName,
    model: data !== null && data?.length > 2 ? data[2]?.trim() : '',
    vin: data !== null && data?.length > 3 ? data[3]?.trim() : ''
  };
};

/**
 * Checks validity of phone numbers (mobile)
 * @param {string} phone - contact number in E.164 format "i.e. +919724665864"
 * @returns {boolean} true or false as per check passes
 */
export const checkPhone = phone => /^\+[1-9]\d{6,14}$/?.test(phone);
export const checkEmail = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(email);

/**
 * Checks for numeric values
 * @param {string|number} no
 */
export const isNumeric = no => {
  return !isNaN(parseFloat(no)) && isFinite(no);
};

const toUsCurrency = no => no?.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
export { toUsCurrency };

export const toUsCurrencyNoZero = no => {
  if (Number.isInteger(no)) {
    return new Intl.NumberFormat('en-us', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(no);
  } else {
    return new Intl.NumberFormat('en-us', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(no);
  }
};

export const getPriceFromMsrp = (msrp, isNumeric = false) => {
  const currency = msrp || '0';
  const priceNumber = Number(currency?.replace(/[^0-9.-]+/g, ''));
  let priceStr = priceNumber > 0 ? toUsCurrency(priceNumber) : '-';
  return isNumeric ? priceNumber : priceStr;
};

/**
 * Returns human readable path of tree-node
 * @param {string} path - path of node
 * @returns {string}
 */
export const getNodeName = (path = '') =>
  path
    ?.split('|')
    ?.join('/')
    ?.replace(/(\d{4})-(\d{1,2})-(\d{1,2})/, '$2.$3.$1');

/**
 * Checks for value is undefined or null or empty string
 * @param {any} val
 */
export const isEmpty = val => val === undefined || val == null || val.length <= 0;

export const getWeekDays = startDate => {
  const days = [startDate];
  for (let i = 1; i < 7; i += 1) {
    days.push(moment(startDate).add(i, 'days').toDate());
  }
  return days;
};

export const getWeekRange = date => {
  const obj = {
    from: moment(date).startOf('week').toDate(),
    to: moment(date).endOf('week').toDate()
  };
  return obj;
};

export const isDateInWeekRange = (date, weekRange) => {
  return weekRange?.findIndex(m => moment(m).format(DATE_FORMAT_YMD) === date) === -1 ? false : true;
};

export const isValidHttpUrl = string => {
  let url;
  try {
    url = new URL(string);
  } catch (e) {
    return false;
  }
  return url.protocol === 'http:' || url.protocol === 'https:';
};

export const getPasswordMessages = pass => {
  let errMsg = [];
  pass = pass ? pass : '';
  if (pass?.length < 8 || pass?.length > 20) {
    errMsg.push(` - Total 8 to 20 characters in length.`);
  }
  if (pass?.search(/[A-Z]/) < 0) {
    errMsg.push(` - At least 1 capital letter.`);
  }
  if (pass?.search(/[a-z]/) < 0) {
    errMsg.push(` - At least 1 small letter.`);
  }
  if (pass?.search(/[0-9]/) < 0) {
    errMsg.push(` - At least 1 digit.`);
  }
  if (pass?.search(/[!@#$%^&*]/) < 0) {
    errMsg.push(` - At least 1 symbol.`);
  }
  if (errMsg?.length) {
    errMsg = ['Password policy:', ...errMsg];
  }
  return errMsg;
};

export const checkDimensions = (file, width = 1536, height = 1024) => {
  return new Promise((resolve, reject) => {
    var reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = function (e) {
      var image = new Image();
      image.src = e.target.result;
      image.onload = function () {
        if (this.height !== height || this.width !== width) {
          let err = new Error(`Invalid image dimensions. It must be ${width}x${height}.`);
          return reject(err);
        }
        return resolve(true);
      };
    };
  });
};

export const parseQuerySearchParams = queryString => {
  let query = {};
  const pairs = (queryString[0] === '?' ? queryString?.substr(1) : queryString)?.split('&');
  for (let i = 0; i < pairs.length; i++) {
    let pair = pairs[i]?.split('=');
    query[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || '');
  }
  return query;
};

export const arrayDiff = (arr1, arr2) =>
  arr1.filter(e => !arr2.includes(e)).concat(arr2.filter(r => !arr1.includes(r)));

// copy text to clipboard
export const copyText = (text, message = 'Copied to clipboard.') => {
  if (window.isSecureContext && window.navigator.clipboard) {
    window.navigator.clipboard
      .writeText(text)
      .then(() => showMsg(message, 'success'))
      .catch(() => showMsg('Oops you have incompitable browser.', 'error'));
  } else {
    const textInput = document.createElement('input');
    textInput.setAttribute('value', text);
    document.body.appendChild(textInput);
    textInput.select();
    document.execCommand('copy');
    document.body.removeChild(textInput);
    showMsg(message, 'success');
  }
};

//Check Ip address validation
export const checkIP = ipaddress => {
  const ipRegEx =
    /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
  return ipRegEx.test(ipaddress);
};
// Phone number convert in international formate
export const phoneformatInternational = number => {
  const phoneNumberUtil = window.libphonenumber;
  const isValid = phoneNumberUtil?.isValidNumber(number);
  let iNumber = number;
  if (isValid) {
    const parsedNumber = phoneNumberUtil.parsePhoneNumber(number);
    iNumber = parsedNumber.formatInternational();
  } else if (number.length <= 10 && number !== 'unknown') {
    const parsedNumber = phoneNumberUtil.parsePhoneNumberFromString(number, 'US');
    iNumber = parsedNumber.formatNational();
  }
  return iNumber;
};

// Formatting number like (202) 456-2121
export const formatValidPhone = phoneStr => {
  try {
    const phoneNumber = window.libphonenumber.parsePhoneNumberFromString(phoneStr, 'US');

    if (phoneNumber && phoneNumber.isValid()) {
      return phoneNumber.formatNational();
    }
    return phoneStr;
  } catch (e) {
    return phoneStr;
  }
};

export const isNullOrEmptyArray = arr => {
  return arr === null || (Array.isArray(arr) && arr.every(item => item === '' || item === null));
};

export const isNumberWithDotAndHyphen = str => {
  const numericSettingRegex = /^[0-9.-]+$/;
  return numericSettingRegex?.test(str);
};

export const isHexColor = colorName => /^#[0-9A-F]{6}$/i.test(colorName);

export const isValidS3Key = key => /^[0-9A-Za-z!_.*' ()-]+(?:\/[0-9A-Za-z!_.*' ()-]+)?$/.test(key);
export const sanitizeS3Keys = key => key.replace(/^./, '').replace(/^\/|\/$/g, '');

export const maskLabel = label => {
  if (typeof label !== 'string') {
    return '';
  }

  if (label.length <= 1) {
    return label;
  }

  return label.slice(0, 1) + '*'.repeat(label.length - 1);
};

export const checkDemoMode = () => {
  const val = localStorage.getItem(STORAGE_TOGGLE_SALES_DEMO_MODE);
  if (val && typeof val === 'string') {
    return JSON.parse(val);
  }
  return false;
};

export const displayDealershipName = name => {
  const isDemoMode = checkDemoMode();

  if (name) {
    if (isDemoMode) {
      return maskLabel(name);
    }
    return name;
  }
  return '';
};

export const maskVinName = (fullName, vin) => {
  const isDemoMode = checkDemoMode();
  if (isDemoMode) {
    if (fullName) {
      return fullName.replace(/\)(.+)$/, `)*****************`);
    }
    if (vin) {
      return vin.replace(vin, `*****************`);
    }
  }
  return fullName || vin;
};
